import React, { useEffect, useRef, useState } from "react";
import { ViewMode } from "gantt-task-react";
import {
  CustomTask,
  DataResponse,
  Item,
  TaskState,
  Workpack,
  EmailOption,
} from "./helper";
import "gantt-task-react/dist/index.css";
import "react-datepicker/dist/react-datepicker.css";
import "./App.css";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  fetchSitesData,
  fetchWorkpackData,
  fetchProjectsData,
  fetchTaskChartData,
  fetchTasks,
  updateTaskWithProgress,
  fetchSiteChartData,
  rebaselineWorkpackData,
  getEmails,
  updateTaskReorder,
} from "./Api";
import DropdownComponent from "./components/Dropdowns";
import GanttChartComponent from "./components/GanttChart";
import ChartComponent from "./components/ChartComponent";
import JobcardModal from "./components/JobcardModal";
import SignatureModal from "./components/SignatureModel";
import OnDemandDialog from "./components/OnDemandDialog";
import { DateTime } from "luxon";

const App = () => {
  const [view, setView] = useState<ViewMode>(ViewMode.Day);
  const [taskDisplay, setTaskDisplay] = useState<CustomTask[]>([]);
  const [isGanttLoading, setIsGanttLoading] = useState(false);

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [histogramLoading, setHistogramLoading] = useState(false);
  const [timeFrame, setTimeFrame] = useState<string>("weekly");
  const [sitesDropdownData, setSitesDropdownData] = useState<Item[]>([]);
  const [projectsDropdownData, setProjectsDropdownData] = useState<Item[]>([]);
  const [workpacksDropdownData, setWorkpacksDropdownData] = useState<Item[]>(
    []
  );
  const [selectedSite, setSelectedSite] = useState<Item>();
  const [selectedProject, setSelectedProject] = useState<Item>();
  const [selectedWorkpack, setSelectedWorkpack] = useState<Item>();
  const [data, setData] = useState<DataResponse | null | undefined>(undefined);
  const [modalShow, setModalShow] = React.useState(false);
  const [signmodalShow, setsignModalShow] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState<CustomTask | null>(
    null
  );
  const [workpackData, setworkpackData] = React.useState<Workpack | null>(null);
  const prevSiteselected = useRef<Item>();
  const prevProjectselected = useRef<Item>();
  const prevTimeFrame = useRef<string>();
  const prevWorkpackselected = useRef<Item>();
  const today = new Date();
  const startYear = today.getFullYear();
  const endYear = startYear + 5;
  const [taskState, setTaskState] = useState<TaskState>({});
  const [isTaskUpdating, setIsTaskUpdating] = useState(false);
  const [email, setEmails] = useState<EmailOption[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isWorkpacksLoading, setIsWorkpacksLoading] = useState(false);
  const toastRef = useRef<Toast | null>(null);
  const [isSitesLoading, setIsSitesLoading] = useState(false);

  useEffect(() => {
    if (sitesDropdownData.length === 0) {
      setIsSitesLoading(true);
      fetchSitesData()
        .then((data) => setSitesDropdownData(data))
        .catch((error) => console.error("Error fetching project list:", error))
        .finally(() => setIsSitesLoading(false));
    }
  }, []);

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const emails = await getEmails();
        setEmails(emails);
      } catch (error) {
        console.error("Error fetching emails:", error);
      }
    };
    fetchEmails();
  }, []);

  useEffect(() => {
    if (selectedSite === null) {
      setProjectsDropdownData([]);
      setWorkpacksDropdownData([]);
      setIsProjectsLoading(false);
      return;
    }
    if (selectedSite && prevSiteselected.current !== selectedSite) {
      setTaskDisplay([]);
      setSelectedWorkpack(undefined);
      setSelectedProject(undefined);
      setData(undefined);
      setWorkpacksDropdownData([]);
      setIsProjectsLoading(true);
      fetchProjectsData(selectedSite.id)
        .then((data) => setProjectsDropdownData(data))
        .catch((error) =>
          console.error("Error fetching second dropdown data:", error)
        )
        .finally(() => setIsProjectsLoading(false));
    }
    prevSiteselected.current = selectedSite;
  }, [selectedSite]);

  useEffect(() => {
    if (selectedProject === null) {
      setProjectsDropdownData([]);
      setWorkpacksDropdownData([]);
      setTaskDisplay([]);
      setSelectedWorkpack(undefined);
      setIsWorkpacksLoading(false);
      return;
    }
    if (selectedProject && prevProjectselected.current !== selectedProject) {
      setTaskDisplay([]);
      setSelectedWorkpack(undefined);
      setData(undefined);
      setIsWorkpacksLoading(true);
      fetchWorkpackData(selectedProject.id)
        .then((data) => setWorkpacksDropdownData(data))
        .catch((error) =>
          console.error("Error fetching third dropdown data:", error)
        )
        .finally(() => setIsWorkpacksLoading(false));
    }
    prevProjectselected.current = selectedProject;
  }, [selectedProject]);

  useEffect(() => {
    if (selectedWorkpack === undefined && workpacksDropdownData.length > 0) {
      setTaskDisplay([]);
      setIsWorkpacksLoading(false);
      return;
    }
    if (selectedWorkpack && prevWorkpackselected.current !== selectedWorkpack) {
      fetchTaskData(selectedWorkpack.id);
    }
    prevWorkpackselected.current = selectedWorkpack;
  }, [selectedWorkpack]);

  useEffect(() => {
    if (!selectedSite && !selectedProject && !selectedWorkpack) {
      setHistogramLoading(false);
      setData(undefined);
      return;
    }
    const workpackAvailable = selectedWorkpack && selectedWorkpack.id !== null;
    const projectAvailable = selectedProject && selectedProject.id !== null;
    const siteAvailable = selectedSite && selectedSite.id !== null;
    if (
      workpackAvailable ||
      (workpackAvailable && prevTimeFrame.current !== timeFrame)
    ) {
      setHistogramLoading(true);
      fetchTaskChartData(selectedWorkpack.id, timeFrame)
        .then((data) => setData(data))
        .catch((error) => {
          console.error("Error fetching chart data:", error);
          setData(null);
        })
        .finally(() => setHistogramLoading(false));
    } else if (
      projectAvailable ||
      (projectAvailable && prevTimeFrame.current !== timeFrame)
    ) {
      setHistogramLoading(true);
      fetchTaskChartData(selectedProject.id, timeFrame)
        .then((data) => setData(data))
        .catch((error) => {
          console.error("Error fetching chart data:", error);
          setData(null);
        })
        .finally(() => setHistogramLoading(false));
    } else if (
      siteAvailable ||
      (siteAvailable && prevTimeFrame.current !== timeFrame)
    ) {
      setHistogramLoading(true);
      fetchSiteChartData(selectedSite.id, timeFrame)
        .then((data) => setData(data))
        .catch((error) => {
          console.error("Error fetching chart data:", error);
          setData(null);
        })
        .finally(() => setHistogramLoading(false));
    }
    prevWorkpackselected.current = selectedWorkpack;
    prevProjectselected.current = selectedProject;
    prevSiteselected.current = selectedSite;
    prevTimeFrame.current = timeFrame;
  }, [selectedProject, selectedSite, selectedWorkpack, timeFrame]);

  const setDate = (task: any) => {
    setSelectedTask(task);
    setModalShow(true);
  };

  // -----------------------
  // Helpers for date-only
  // -----------------------
  const stripTime = (d: Date) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return nd;
  };

  // Format local YYYY-MM-DD (Option A)
  const formatLocalDate = (date: Date) => {
    const d = stripTime(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  const fetchTaskData = (wpId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      fetchTasks(wpId)
        .then((data) => {
          setworkpackData(data.workpack);
          var isDisabled = false;
          const tasksWithDates = data.data.map((task) => {
            // Parse incoming dates then strip time (use Luxon to parse ISO, then toJSDate)
            const formatStartDate = (dateStr: string | Date | null) => {
              if (!dateStr) {
                return stripTime(new Date());
              }
              // if API returns YYYY-MM-DD or ISO, Luxon handles both
              return stripTime(DateTime.fromISO(dateStr.toString()).toJSDate());
            };
            const formatEndDate = (
              dateStr: string | Date | null,
              startDate: Date
            ) => {
              if (!dateStr) {
                const d = stripTime(startDate);
                return d;
              }
              return stripTime(DateTime.fromISO(dateStr.toString()).toJSDate());
            };
            const startDate = formatStartDate(task.start);
            const endDate = formatEndDate(task.end, startDate);
            return {
              ...task,
              start: startDate,
              end: endDate,
              isDisabled: isDisabled,
              is_locked: task.is_locked ?? false,
              progressHandleVisible: !task.is_locked,
              hideChildren: taskState[task.id] || false,
              styles: {
                progressColor: isDisabled ? "#636363" : "#8282f5",
                progressSelectedColor: isDisabled ? "#636363" : "#7373fa",
              },
            };
          });
          setTaskDisplay([...tasksWithDates]);
          resolve();
        })
        .catch((error) => {
          console.error("Error fetching tasks:", error);
          setTaskDisplay([]);
          reject(error);
        })
        .finally(() => setIsTaskUpdating(false));
    });
  };

  useEffect(() => {
    try {
      const storedLocks = JSON.parse(localStorage.getItem("taskLocks") || "{}");
      setTaskDisplay((prev) =>
        prev.map((t) => ({
          ...t,
          is_locked: storedLocks[t.id] ?? t.is_locked,
        }))
      );
    } catch (error) {
      console.error("Error parsing task locks from localStorage:", error);
      setTaskDisplay((prev) =>
        prev.map((t) => ({
          ...t,
          is_locked: t.is_locked,
        }))
      );
    }
  }, []);

  function isDate(value: any): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  // compute date diff using local dates (set hours to 0)
  const getDateDiff = (newDate: Date, oldDate: Date): number => {
    const newDay = stripTime(newDate);
    const oldDay = stripTime(oldDate);
    const diffTime = newDay.getTime() - oldDay.getTime();
    return diffTime / (1000 * 60 * 60 * 24);
  };

  const findDependentTasks = (
    taskId: string,
    allTasks: CustomTask[],
    visited = new Set<string>()
  ): CustomTask[] => {
    const dependents: CustomTask[] = [];
    allTasks.forEach((task) => {
      if (
        task.dependencies &&
        task.dependencies.includes(taskId) &&
        !visited.has(task.id)
      ) {
        visited.add(task.id);
        dependents.push(task);
        dependents.push(...findDependentTasks(task.id, allTasks, visited));
      }
    });
    return dependents;
  };

  const updateTaskDates = (task: CustomTask, dayOffset: number): CustomTask => {
    const newStart = stripTime(new Date(task.start));
    newStart.setDate(newStart.getDate() + dayOffset);
    const newEnd = stripTime(new Date(task.end));
    newEnd.setDate(newEnd.getDate() + dayOffset);
    return {
      ...task,
      start: newStart,
      end: newEnd,
    };
  };

  // run rebaseline first, then update tasks (ensures backend won't overwrite our changes)
  const runRebaselineThenUpdate = async (tasksToUpdate: CustomTask[]) => {
    setIsTaskUpdating(true);
    try {
      await rebaselineWorkpackData(selectedWorkpack?.id!);
    } catch (err) {
      console.error("Rebaseline failed:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Rebaseline failed",
        detail: "Failed to rebaseline. Please try again.",
        life: 5000,
      });
      // revert UI
      await fetchTaskData(selectedWorkpack?.id!);
      return;
    }
    // now update tasks (this will show loader)
    await updateAffectedTasks(tasksToUpdate);
  };

  // Central handler
  const handleTaskChange = (task: CustomTask) => {
    const original = taskDisplay.find((t) => t.id === task.id);
    if (!original) return;

    const startOffset = Math.round(getDateDiff(task.start, original.start));
    const endOffset = Math.round(getDateDiff(task.end, original.end));

    const isMoving =
      (startOffset > 0 && endOffset > 0) || (startOffset < 0 && endOffset < 0);

    const isResizing = !isMoving && (startOffset !== 0 || endOffset !== 0);

    const isprogresschanged =
      task.progress !== original.progress ||
      task.is_locked !== original.is_locked;

    const changed = isMoving || isResizing || isprogresschanged;
    if (!changed) return;

    // find dependents
    const deps = findDependentTasks(task.id, taskDisplay);

    // preview local UI changes (no backend)
    const updatedTasks = taskDisplay.map((t) => {
      if (t.id === task.id) return task;
      const dep = deps.find((d) => d.id === t.id);
      if (!dep) return t;
      // Only move dependents on a MOVE. Do NOT resize dependents.
      if (isMoving && startOffset !== 0) return updateTaskDates(t, startOffset);
      return t;
    });

    // helper to pick the correct updated objects for backend
    const getUpdatedBackendTasks = (includeDependents: boolean) => {
      return updatedTasks.filter((t) => {
        if (t.id === task.id) return true;
        if (includeDependents && deps.some((d) => d.id === t.id)) return true;
        return false;
      });
    };

    // Only show rebaseline when workpack is full rebaseline mode
    const fullRebaselineMode =
      !!workpackData?.construction &&
      !!workpackData?.offshore &&
      !!workpackData?.onshore;

    if (fullRebaselineMode && (isMoving || isResizing)) {
      confirmDialog({
        message: "Do you want to rebaseline?",
        header: "Rebaseline Confirmation",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "accept",

        accept() {
          if (deps.length === 0 || isResizing) {
            // user wants to rebaseline but there are no deps => run rebaseline then update main task
            runRebaselineThenUpdate(getUpdatedBackendTasks(false));
            return;
          }

          // show second dialog after a tiny delay so the first dialog fully closes
          if (deps.length > 0 && isMoving) {
            setTimeout(() => {
              confirmDialog({
                message: `This task has ${deps.length} dependent task(s). Move dependent tasks also?`,
                header: "Move Dependent Tasks",
                icon: "pi pi-exclamation-triangle",
                defaultFocus: "accept",

                accept() {
                  runRebaselineThenUpdate(getUpdatedBackendTasks(true));
                },

                reject() {
                  // Option 1 behavior: NO => update only main task (using updated object)
                  runRebaselineThenUpdate(getUpdatedBackendTasks(false));
                },
              });
            }, 100);
          }
        },

        reject() {
          // revert UI changes
          fetchTaskData(selectedWorkpack?.id!);
          setIsTaskUpdating(false);
        },
      });

      // IMPORTANT: stop execution here so we don't run the non-rebaseline branch
      return;
    }
    if (isprogresschanged) {
      updateAffectedTasks(getUpdatedBackendTasks(false));
      return;
    }
    // Non-rebaseline mode: ask only about dependents then update
    if (deps.length > 0 && isMoving) {
      confirmDialog({
        message: `This task has ${deps.length} dependent task(s). Move dependent tasks also?`,
        header: "Move Dependent Tasks",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "accept",
        accept() {
          updateAffectedTasks(getUpdatedBackendTasks(true));
        },
        reject() {
          // Option 1 behavior: NO => update only main task (using updated object)
          updateAffectedTasks(getUpdatedBackendTasks(false));
        },
      });
    } else {
      updateAffectedTasks(getUpdatedBackendTasks(false));
    }
  };

  // updateAffectedTasks shows loader and performs PATCH calls
  const updateAffectedTasks = (tasksToUpdate: CustomTask[]) => {
    setIsTaskUpdating(true);

    const updatePromises = tasksToUpdate.map((taskToUpdate) => {
      if (!isDate(taskToUpdate.start) || !isDate(taskToUpdate.end)) {
        return Promise.resolve();
      }

      const data = {
        start_date: formatLocalDate(taskToUpdate.start),
        end_date: formatLocalDate(taskToUpdate.end),
        id: taskToUpdate.id,
        progress: Math.round(taskToUpdate.progress ?? 0),
        is_locked: taskToUpdate.is_locked,
        parent: taskToUpdate.project,
      };
      return updateTaskWithProgress(data);
    });

    return Promise.all(updatePromises)
      .then(() => {
        toastRef.current?.show({
          severity: "success",
          summary: "Updated",
          detail: "Task(s) updated successfully.",
          life: 3000,
        });
      })
      .catch((error) => {
        console.error("Error updating tasks in backend:", error);
        toastRef.current?.show({
          severity: "error",
          summary: "Update failed",
          detail: "Failed to update task(s). Please try again.",
          life: 5000,
        });
      })
      .finally(() => {
        Promise.all([
          fetchWpHistogram(),
          fetchTaskData(selectedWorkpack?.id!),
        ]).finally(() => {
          setIsTaskUpdating(false);
        });
      });
  };

  const handleProgressTaskChange = (task: CustomTask) => {
    if (task.is_locked) return;
    setIsTaskUpdating(true);
    const newTasks = taskDisplay.map((t) => (t.id === task.id ? task : t));
    setTaskDisplay(newTasks);
    if (isDate(task.start) && isDate(task.end)) {
      updateTaskWithProgress({
        id: task.id,
        progress: Math.round(task.progress ?? 0),
        is_locked: task.is_locked,
        parent: task.project,
      })
        .catch((error) => {
          console.error("Error updating task with progress:", error);
          toastRef.current?.show({
            severity: "error",
            summary: "Progress update failed",
            detail: "Failed to update task progress. Please try again.",
            life: 5000,
          });
        })
        .finally(() => {
          Promise.all([
            fetchWpHistogram(),
            fetchTaskData(selectedWorkpack?.id!),
          ]).finally(() => {
            setIsTaskUpdating(false);
          });
        });
    } else {
      setIsTaskUpdating(false);
    }
  };

  const fetchWpHistogram = () => {
    const workpackAvailable = selectedWorkpack && selectedWorkpack.id !== null;
    if (
      workpackAvailable ||
      (workpackAvailable && prevTimeFrame.current !== timeFrame)
    ) {
      fetchTaskChartData(selectedWorkpack.id, timeFrame)
        .then((data) => setData(data))
        .catch((error) => console.error("Error fetching chart data:", error))
        .finally(() => setHistogramLoading(false));
    }
  };
  const onExpanderClick = (task: any) => {
    setTaskState((prev) => ({
      ...prev,
      [task.id]: !prev[task.id],
    }));
    setTaskDisplay(taskDisplay.map((t) => (t.id === task.id ? task : t)));
  };
  function openSign(): void {
    setsignModalShow(true);
  }
  const handleReorderComplete = () => {
    if (selectedWorkpack?.id) {
      setIsTaskUpdating(true);
      fetchTaskData(selectedWorkpack.id).finally(() => {
        setIsTaskUpdating(false);
      });
    }
  };

  return (
    <div>
      <Toast ref={toastRef} />
      {isTaskUpdating && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              fontSize: "2rem",
              fontWeight: 600,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              color: "#ffffff",
              textShadow: "0 0 2px #000",
            }}
          >
            <span>Loading...</span>
            <ProgressSpinner style={{ width: "40px", height: "40px" }} />
          </div>
        </div>
      )}
      <DropdownComponent
        sitesDropdownData={sitesDropdownData}
        projectsDropdownData={projectsDropdownData}
        workpacksDropdownData={workpacksDropdownData}
        setSelectedSite={setSelectedSite}
        setSelectedProject={setSelectedProject}
        setSelectedWorkpack={setSelectedWorkpack}
        selectedSite={selectedSite}
        selectedProject={selectedProject}
        selectedWorkpack={selectedWorkpack}
        isSitesLoading={isSitesLoading}
        isProjectsLoading={isProjectsLoading}
        isWorkpacksLoading={isWorkpacksLoading}
      />
      <div className="block">
        <ChartComponent
          timeFrame={timeFrame}
          data={data}
          histogramloading={histogramLoading}
          setTimeFrame={setTimeFrame}
        />
      </div>
      <GanttChartComponent
        workpack={workpackData}
        taskdisplay={taskDisplay}
        isganttLoading={isGanttLoading}
        handleTaskChange={handleTaskChange}
        view={view}
        openSign={openSign}
        setView={setView}
        handleTaskDelete={() => true}
        handleProgressChange={handleProgressTaskChange}
        handleDblClick={() => {}}
        openTemplate={() => setDialogVisible(true)}
        handleClick={() => {}}
        handleSelect={() => {}}
        onExpanderClick={onExpanderClick}
        handleAddTask={() => {}}
        setDate={setDate}
        isTaskUpdating={isTaskUpdating}
        setIsTaskUpdating={setIsTaskUpdating}
        onReorderComplete={handleReorderComplete}
      />
      <JobcardModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        task={selectedTask}
        fetchData={() => {
          if (selectedWorkpack) {
            setIsTaskUpdating(true);
            fetchWpHistogram();
            fetchTaskData(selectedWorkpack?.id);
          }
        }}
        handleTaskChange={handleTaskChange}
      />
      <SignatureModal
        show={signmodalShow}
        workpack={workpackData}
        onHide={() => setsignModalShow(false)}
        taskId={selectedWorkpack?.id}
        allEmail={email}
        projectId={selectedProject?.name ?? null}
        siteId={selectedSite?.name ?? null}
        workpackId={selectedWorkpack?.name ?? null}
        fetchData={() => {
          if (selectedWorkpack) {
            setIsTaskUpdating(true);
            fetchTaskData(selectedWorkpack?.id);
          }
        }}
      />
      <OnDemandDialog
        visible={dialogVisible}
        projectId={selectedProject?.id ?? ""}
        onHide={() => setDialogVisible(false)}
      />
      <ConfirmDialog />
    </div>
  );
};

export default App;